"use client";
import FailedEmailVerification from "@components/ui/ecco-club/cards/FailedEmailVerification";
import MemberBenefits from "@components/ui/ecco-club/cards/MemberOnlyBenefits";
import MembershipDetails from "@components/ui/ecco-club/cards/MembershipDetails";
import MembershipDetailsSkeleton from "@components/ui/ecco-club/cards/MembershipDetailsSkeleton";
import PendingEmailVerification from "@components/ui/ecco-club/cards/pendingEmailVerification";
import SuccessMessage from "@components/ui/ecco-club/cards/SuccessMessage";
import styles from "@components/ui/ecco-club/faqSection.module.css";
import type { IPageTwoColumns } from "@contentful-api/types/contentful";
import type { Customer } from "@lib/api-services/services/profile-service/types";
import { useAuth } from "@lib/auth/useAuth";
import { CommonCMS } from "@lib/constants/contentful";
import { getPagePathByLocale, myAccountEccoClubPage } from "@lib/constants/pagePaths";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { QueryKeys } from "@lib/enums/QueryKeys";
import { ClientFetchService } from "@lib/helpers/clientFetchService";
import { useApi } from "@lib/helpers/useApi";
import { isSerbianSignup } from "@lib/utils/ecco-club/serbianConfig";
import { useStore } from "@store/store";
import { Heading } from "@ecco/ui/heading";
import { Text } from "@ecco/ui/text";
import { Box } from "@ecco/ui/box";
import { useRouter } from "@i18n/routing";
import { useLocale } from "next-intl";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { TwoColumnsPage } from "@templates/TwoColumnPage";

interface AccountVerificationClientProps {
    page: IPageTwoColumns;
    isSerbianMarket?: boolean;
}

export default function AccountVerificationClient({
    page,
    isSerbianMarket,
}: AccountVerificationClientProps) {
    const searchParams = useSearchParams();
    const { push } = useRouter();
    const locale = useLocale();
    const getTempCustomerData = useStore((state) => state.getTempCustomerData);

    const registrationInitiated = searchParams.get("registrationInitiated");
    const emailVerificationId = searchParams.get("emailVerificationId");
    const market = searchParams.get("market");
    const { firstName, lastName, email } = getTempCustomerData();

    const [isHydrated, setIsHydrated] = useState(false);
    const { session, isLoggedIn, signOut, signIn } = useAuth();

    const { getMultiple } = useMicrocopy();

    const microcopies = (() => {
        return getMultiple(isSerbianMarket ? CommonCMS.page : CommonCMS.shared, [
            CommonCMS.loyalty.accessRewardsSubtitle,
            CommonCMS.loyalty.emailVerification.emailSent,
            CommonCMS.loyalty.emailVerification.verifyEmailToGetAccess,
            CommonCMS.loyalty.emailVerification.failed,
            CommonCMS.welcomeToEccoClub,
        ]);
    })();

    useEffect(() => {
        setIsHydrated(true);
    }, []);

    const getUser = async () => {
        return await ClientFetchService.get<Customer & { error?: string }>(`/api/user/get-user`);
    };

    const {
        data: verificationData,
        isLoading: isVerifying,
        error,
    } = useApi.useAsync(
        [QueryKeys.verifyEmail, emailVerificationId],
        async () => {
            if (emailVerificationId) {
                const { error } = await signIn("emailVerification", {
                    redirect: false,
                    emailVerificationId,
                    market: market || null,
                    sessionId: session?.sessionId || null,
                });
                if (error) {
                    if (isLoggedIn) {
                        push(getPagePathByLocale(myAccountEccoClubPage, locale));
                    } else {
                        throw new Error();
                    }
                }
                if (isSerbianSignup(market, locale)) {
                    // customer in Serbia should not be logged in at all after the email verification
                    await signOut();
                    return {
                        success: true,
                    };
                }
                const customer = await getUser();
                if (customer.error) {
                    throw new Error();
                }
                return {
                    tierLevel: customer.loyalty?.tier,
                    pointsBalance: customer.loyalty?.pointBalance,
                    membershipId: customer.loyalty?.cardNumber,
                };
            }
        },
        {
            enabled: !!emailVerificationId,
        }
    );

    const renderMainContent = () => {
        if (!emailVerificationId && !(registrationInitiated && firstName && lastName)) {
            return <MembershipDetailsSkeleton />;
        }
        if (emailVerificationId) {
            if (isVerifying) {
                return <MembershipDetailsSkeleton />;
            }
            if (error) {
                return <FailedEmailVerification microcopies={microcopies} />;
            }
            if (isSerbianSignup(market, locale)) {
                return <SuccessMessage />;
            }
            if (verificationData) {
                return (
                    <MembershipDetails
                        tierLevel={verificationData.tierLevel}
                        pointsBalance={verificationData.pointsBalance}
                        membershipId={verificationData.membershipId}
                    />
                );
            }
        }

        if (registrationInitiated && firstName && lastName) {
            return <PendingEmailVerification microcopies={microcopies} email={email} />;
        }

        return null;
    };

    return (
        <Box as="main">
            <Box background={"gray.100"}>
                <Heading
                    as="h1"
                    textTransform="capitalize"
                    pb={[2, 4]}
                    pt={[5, 8]}
                    ml={["layoutMargin.mobile", "layoutMargin.tab", "layoutMargin.desktop"]}
                >
                    {isHydrated && firstName && lastName ? `Hi, ${firstName} ${lastName}` : "Hi"}
                </Heading>
                <Box
                    className="subheading"
                    pb={[3, 6]}
                    ml={["layoutMargin.mobile", "layoutMargin.tab", "layoutMargin.desktop"]}
                >
                    <Text>
                        {verificationData
                            ? microcopies[CommonCMS.welcomeToEccoClub]
                            : microcopies[CommonCMS.loyalty.accessRewardsSubtitle]}
                    </Text>
                </Box>
            </Box>
            <Box className={styles.faqWrapper}>
                <TwoColumnsPage
                    showTitle={false}
                    navigationTitle={page?.fields?.title}
                    page={page}
                    leftColumn={{ prefix: <Box pr={[3, 4, 6, 0]}>{renderMainContent()}</Box> }}
                />
            </Box>
            <Box bg="gray.50" paddingBottom={[8, 12]}>
                <MemberBenefits isSerbianMarket={isSerbianMarket} />
            </Box>
        </Box>
    );
}
